import React from 'react';

import type { MediaWrapperContainerTypeEnum } from '@/utils/aspectRatioUtils';

import { Card } from '@/cms/Card';
import { CMSSection } from '@/cms/CMSSection';
import type { ProductCardProps } from '@/cms/ProductCard';
import { ProductCard } from '@/cms/ProductCard';
import type { MediaWrapperProps } from '@/components/Cms/MediaWrapper';
import { MediaWrapper } from '@/components/Cms/MediaWrapper';
import { Container } from '@/components/Container';
import type {
  ContentfulCardFragment,
  ContentfulContentGroupFragment,
} from '@/graphql/contentful/api';
import type { CmsExtraDataRecord } from '@/integrations/contentful';
import { generateSectionKey } from '@/integrations/contentful';
import type {
  ContentfulData,
  ContentType,
} from '@/integrations/contentful/types';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe, WithHTMLProp } from '@/lib/types';

import type { CardProps } from '../Card';

import * as S from './ThreeColumnStack.styles';

type ThreeColumnStackWrapperProps = ContentfulContentGroupFragment & {
  extraDataRecord?: CmsExtraDataRecord;
  type?: Maybe<string>;
} & WithHTMLProp<'className'>;

export interface WallItemProps extends ContentfulCardFragment {
  preload?: boolean;
}
export const ThreeColumnStack = ({
  className,
  extraDataRecord,
  itemsCollection,
  type,
}: ThreeColumnStackWrapperProps) => {
  return (
    <CMSSection
      className={className}
      {...generateDataTestId('contentCollection', 'section')}
      data-test-id='ThreeColumnStackWrapperSection'
    >
      <Container>
        <S.Wrapper data-test-id='ThreeColumnStackWrapper'>
          {itemsCollection?.items.filter(Boolean).map((item, i, array) => {
            const props = (item ? { ...item } : null) as ItemData;
            if (props) {
              props.preload = true;
            }
            const renderedItem = renderCollectionItem(
              props,
              i,
              array.length,
              extraDataRecord,
              type,
            );
            return renderedItem ? (
              renderCollectionItem(
                item as ItemData,
                i,
                array.length,
                extraDataRecord,
                type,
              )
            ) : (
              <></>
            );
          })}
        </S.Wrapper>
      </Container>
    </CMSSection>
  );
};

type ItemData = CardProps | MediaWrapperProps | ProductCardProps | null;

export const renderCollectionItem = (
  data: ItemData,
  i: number,
  arrayLength: number,
  extraDataRecord?: CmsExtraDataRecord,
  type?: Maybe<string>,
) => {
  let props = null;
  const typename = data?.__typename as ContentType;
  const sysId = data?.sys?.id;
  const extraData = extraDataRecord ? extraDataRecord[sysId ?? ''] : undefined;
  switch (typename) {
    case 'Card':
      props = data as WallItemProps;
      return (
        <S.ParameterCol
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`ThreeColumnStackParameterCol-${i}`}
          md={12 / arrayLength}
        >
          <Card
            {...props}
            cardGroupType={type as MediaWrapperContainerTypeEnum}
            extraData={extraData?.extraData}
          />
        </S.ParameterCol>
      );

    case 'ImageWithFocalPoint':
      props = data as MediaWrapperProps;
      return (
        <S.ParameterCol
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`ThreeColumnStackParameterCol-${i}`}
          md={12 / arrayLength}
        >
          <MediaWrapper {...props} mediaWrapperType='ThreeColumnStack' />
        </S.ParameterCol>
      );
    case 'ProductCard':
      props = data as ProductCardProps;
      return (
        <S.ParameterCol
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`ThreeColumnStackParameterCol-${i}`}
          md={12 / arrayLength}
        >
          <ProductCard
            autoLoadCTA={true}
            {...props}
            productData={extraData?.extraData?.productData[0]}
          />
        </S.ParameterCol>
      );
    default:
      return null;
  }
};

ThreeColumnStack.displayName = 'ThreeColumnStack';
